import { IInputScope, KbInput } from "@app/components/kb-input/kb-input.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { eFormatType, eSliderValueVisibility } from "@models";
import { Utils } from "@tools";

export interface ISliderScope extends IInputScope {
    min: number;
    max: number;
    step: number;
    directEdit?: boolean;
    valueVisibility?: eSliderValueVisibility;

    /** see eFormatType: none, number, currency, percentage */
    format: string;
    minPrecision: number;
    maxPrecision: number;
    /** the currency iso code (only used if format = currency) */
    currency: string;
    thousandsSeparator: string;
    decimalSeparator: string;
    prefix: string;
    suffix: string;

    currentVal?: number;
    dragging?: boolean;    
    formattedValue?: string;
    tickPercent?: number; // the percentage step size that ticks will be shown
    lineClick?: (e: JQueryEventObject) => void;
}
@NgComponent({
    token: Token.KbSlider,
    dependencies: [
        Token.$Timeout,
        Token.$Document
    ]
})
export class KbSlider extends KbInput {
    constructor(
        $timeout: ng.ITimeoutService,
        $document: ng.IDocumentService
    ) {
        super($timeout);

        this.scope = Utils.extend(this.scope, {
            min: "=",
            max: "=",
            step: "=?",
            format: "@",
            minPrecision: "@",
            maxPrecision: "@",
            currency: "@",
            thousandsSeparator: "@",
            decimalSeparator: "@",
            prefix: "@",
            suffix: "@",
            directEdit: "=?",
            valueVisibility: "=?"
        });

        this.template = Dirs.component("kb-slider");

        this.link = (
            scope: ISliderScope,
            element: ng.IAugmentedJQuery,
            atts: ng.IAttributes,
            controllers: any[]
        ) => {
            this.kbInputLinkFn(scope, element, atts, controllers);
            let ngModel = controllers[0] as ng.INgModelController;    
            let $thumb = element.find(".kb-slider__thumb");
            let thumbElem = $thumb[0];
            let lineElem = element.find(".kb-slider__line")[0];
            let valueLineElem = element.find(".kb-slider__value-line")[0];

            let setViewValue = (val) => {
                ngModel.$setViewValue(val);
                setFormattedValue(val);
            };

            let getNearestVal = (tryVal: number) => {
                let steps = Math.floor(scope.max / scope.step);
                let val = Math.round(tryVal / scope.step) * scope.step;
                val = Math.min(scope.max, Math.max(val, scope.min));
                return val;
            };

            let setCurrentValue = (val) => {
                scope.currentVal = val;
                return val;
            };
            
            let setThumbPos = (val, withAnimation = true) => {
                let clientWidth = lineElem.clientWidth;
                let percent = (val - scope.min) / (scope.max - scope.min);
                percent = Math.max(Math.min(1, percent), 0);
                let cssVal = `${percent * 100}%`;
                if (withAnimation) {
                    $(thumbElem).stop(true, true).animate({ left: cssVal }, 300, "easeInOutCubic");
                    $(valueLineElem).stop(true, true).animate({ width: cssVal }, 300, "easeInOutCubic");
                } else {
                    thumbElem.style.left = cssVal;
                    valueLineElem.style.width = cssVal;
                }
                
                return val;
            };

            let getValFromMousePos = (mouseClientX: number) => {
                let lineRect = lineElem.getBoundingClientRect();
                let lineWidth = lineRect.right - lineRect.left;
                let percent = (mouseClientX - lineRect.left) / lineWidth;
                let cval = ((scope.max - scope.min) * percent) + scope.min;
                cval = cval.rounder(scope.maxPrecision);
                cval = getNearestVal(cval);
                return cval;
            };

            let setFormattedValue = (value: any) => {
                let format = scope.format;
                if (!format || format == eFormatType.none) format = eFormatType.number;
                scope.formattedValue = (value as number).format({
                    formatType: format,
                    minPrecision: Number(scope.minPrecision),
                    maxPrecision: Number(scope.maxPrecision),
                    currency: scope.currency,
                    decimalSeparator: scope.decimalSeparator,
                    thousandsSeparator: scope.thousandsSeparator,
                    prefix: scope.prefix,
                    suffix: scope.suffix
                });
                return value;
            };

            let setTicks = (val) => {
                scope.tickPercent = Math.min(Math.max(scope.step / (scope.max - scope.min), .01), 1);
                return val;
            };

            //formatters are run in reverse
            ngModel.$formatters.push(setTicks);
            ngModel.$formatters.push(setFormattedValue);
            ngModel.$formatters.push(setThumbPos);
            ngModel.$formatters.push(setCurrentValue);
            ngModel.$formatters.push(getNearestVal);

            scope.lineClick = (e) => {
                if (!scope.ngDisabled) {
                    let cval = getValFromMousePos(e.clientX);
                    setCurrentValue(cval);
                    setThumbPos(cval, true);
                    setViewValue(cval);
                    // scope.ngModel = cval; // set ngmodel instead of setViewValue so that it runs through all the formatters again
                }
            };

            let dragging = false;
            let pointerdown = (e: JQueryEventObject) => {
                if (!scope.ngDisabled) {
                    scope.dragging = true;
                
                    $document.attr("touch-action", "none");
                    $document.on("pointerup", pointerup);
                    $document.on("pointermove", pointermove);

                    scope.$digest();
                }
            };

            let pointerup = (e: JQueryEventObject) => {
                if (!scope.ngDisabled) {
                    scope.dragging = false;
                    $document.attr("touch-action", "");
                    $document.off("pointerup", pointerup);
                    $document.off("pointermove", pointermove);

                    setViewValue(scope.currentVal);
                    scope.$digest();
                }
            };

            let pointermove = (e: JQueryEventObject) => {
                if (!scope.ngDisabled) {
                    let cval = getValFromMousePos(e.clientX);
                    scope.currentVal = cval;
                    setThumbPos(cval, false);
                    setFormattedValue(cval);
                    scope.$digest();
                }
            };            

            let keydown = (e: JQueryEventObject) => {
                if (!scope.ngDisabled) {
                    if (e.key == 'ArrowLeft') { //arrow left
                        let cval = getNearestVal(scope.ngModel - scope.step);
                        scope.currentVal = cval;
                        setThumbPos(cval, false);
                        setViewValue(cval);
                        scope.$digest();
                    } else if (e.key == 'ArrowRight') { //arrow right
                        let cval = getNearestVal(scope.ngModel + scope.step);
                        scope.currentVal = cval;
                        setThumbPos(cval, false);
                        setViewValue(cval);
                        scope.$digest();
                    } else if (e.key == 'Escape') {
                        element.trigger('blur');
                    }
                }
            };

            // we need to add the touch-action attribute to the svg element for the pep library
            // for the pointer-events w3c standard, this will be replaced by a css attribute
            $thumb.attr("touch-action", "none");
            $thumb.on("pointerdown", pointerdown);
            element.on("keydown", keydown);
            
            scope.$watch("min", (newVal) => {
                setThumbPos(scope.ngModel);
                setTicks(scope.ngModel);
            });
            scope.$watch("max", (newVal) => {
                setThumbPos(scope.ngModel);
                setTicks(scope.ngModel);
            });
            scope.$watch("step", (newVal) => {
                setThumbPos(scope.ngModel);
                setTicks(scope.ngModel);
            });

            scope.$on("$destroy", () => {
                element.off();
                $document.off("pointerup", pointerup);
                $document.off("pointermove", pointermove);
            });
        };
    }
}
